import { supabase } from '../lib/supabase';

/**
 * Service for Musical Progress Page data operations
 * Fetches musical details, roles, and songs for student progress tracking
 */
export const musicalProgressService = {
  /**
   * Get complete musical details with roles and songs
   * @param {string} musicalId - Musical ID
   * @returns {Promise<Object>} Complete musical data
   */
  async getMusicalDetails(musicalId) {
    try {
      const { data, error } = await supabase?.from('musicals')?.select(`
          id,
          title,
          poster_url,
          description,
          composer,
          lyricist,
          book_writer,
          year_premiered,
          genre
        `)?.eq('id', musicalId)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        title: data?.title,
        posterUrl: data?.poster_url,
        description: data?.description,
        composer: data?.composer,
        lyricist: data?.lyricist,
        bookWriter: data?.book_writer,
        yearPremiered: data?.year_premiered,
        genre: data?.genre
      };
    } catch (error) {
      console.error('Error fetching musical details:', error);
      throw error;
    }
  },

  /**
   * Get all roles in a musical with user progress
   * @param {string} musicalId - Musical ID
   * @param {string} userId - User ID for progress tracking
   * @returns {Promise<Array>} List of roles with progress indicators
   */
  async getMusicalRoles(musicalId, userId) {
    try {
      const { data, error } = await supabase?.from('roles')?.select(`
          id,
          character_name,
          description,
          voice_type,
          vocal_range,
          age_range,
          is_principal,
          role_type
        `)?.eq('musical_id', musicalId)?.order('is_principal', { ascending: false })?.order('character_name', { ascending: true });

      if (error) throw error;

      // Get user's progress for songs related to each role
      const roleIds = (data || [])?.map(r => r?.id);
      
      const { data: progressData, error: progressError } = await supabase?.from('user_lesson_progress')?.select(`
          song_id,
          lesson_status,
          character_analysis_id,
          character_song_analyses!inner (
            role_id
          )
        `)?.eq('user_id', userId)?.in('character_song_analyses.role_id', roleIds);

      if (progressError) {
        console.warn('Error fetching role progress:', progressError);
      }

      // Map progress to roles
      const progressByRole = {};
      (progressData || [])?.forEach(p => {
        const roleId = p?.character_song_analyses?.role_id;
        if (roleId) {
          if (!progressByRole?.[roleId]) {
            progressByRole[roleId] = { completed: 0, total: 0 };
          }
          progressByRole[roleId].total += 1;
          if (p?.lesson_status === 'completed') {
            progressByRole[roleId].completed += 1;
          }
        }
      });

      return (data || [])?.map(role => ({
        id: role?.id,
        characterName: role?.character_name,
        description: role?.description,
        voiceType: role?.voice_type,
        vocalRange: role?.vocal_range,
        ageRange: role?.age_range,
        isPrincipal: role?.is_principal,
        roleType: role?.role_type,
        progress: progressByRole?.[role?.id] || { completed: 0, total: 0 }
      }));
    } catch (error) {
      console.error('Error fetching musical roles:', error);
      throw error;
    }
  },

  /**
   * Get all songs in a musical with user progress
   * @param {string} musicalId - Musical ID
   * @param {string} userId - User ID for progress tracking
   * @returns {Promise<Array>} List of songs with completion status
   */
  async getMusicalSongs(musicalId, userId) {
    try {
      const { data, error } = await supabase?.from('songs')?.select(`
          id,
          title,
          act_number,
          song_order,
          key_signature
        `)?.eq('musical_id', musicalId)?.order('act_number', { ascending: true })?.order('song_order', { ascending: true });

      if (error) throw error;

      // Get user's progress for these songs
      const songIds = (data || [])?.map(s => s?.id);
      
      const { data: progressData, error: progressError } = await supabase?.from('user_lesson_progress')?.select('song_id, lesson_section, lesson_status, quiz_completed, is_quiz_perfect')?.eq('user_id', userId)?.in('song_id', songIds);

      if (progressError) {
        console.warn('Error fetching song progress:', progressError);
      }

      // Calculate progress for each song (6 sections: overview + level1-5)
      const progressBySong = {};
      (progressData || [])?.forEach(p => {
        if (!progressBySong?.[p?.song_id]) {
          progressBySong[p.song_id] = {
            completed: 0,
            total: 6,
            quizCompleted: 0,
            quizPerfect: 0
          };
        }
        if (p?.lesson_status === 'completed') {
          progressBySong[p.song_id].completed += 1;
        }
        if (p?.quiz_completed) {
          progressBySong[p.song_id].quizCompleted += 1;
        }
        if (p?.is_quiz_perfect) {
          progressBySong[p.song_id].quizPerfect += 1;
        }
      });

      return (data || [])?.map(song => {
        const progress = progressBySong?.[song?.id] || { completed: 0, total: 6, quizCompleted: 0, quizPerfect: 0 };
        const completionPercentage = (progress?.completed / progress?.total) * 100;

        return {
          id: song?.id,
          title: song?.title,
          actNumber: song?.act_number,
          songOrder: song?.song_order,
          keySignature: song?.key_signature,
          completionPercentage: Math.round(completionPercentage),
          lessonsCompleted: progress?.completed,
          totalLessons: progress?.total,
          quizCompleted: progress?.quizCompleted,
          quizPerfect: progress?.quizPerfect
        };
      });
    } catch (error) {
      console.error('Error fetching musical songs:', error);
      throw error;
    }
  },

  /**
   * Get associated roles for a specific song
   * @param {string} songId - Song ID
   * @returns {Promise<Array>} List of roles associated with the song
   */
  async getSongRoles(songId) {
    try {
      const { data, error } = await supabase?.from('song_roles')?.select(`
          role_id,
          is_solo,
          vocal_part,
          roles (
            id,
            character_name,
            voice_type,
            musicals (
              id,
              title,
              poster_url
            )
          )
        `)?.eq('song_id', songId);

      if (error) throw error;

      return (data || [])?.map(sr => ({
        roleId: sr?.role_id,
        isSolo: sr?.is_solo,
        vocalPart: sr?.vocal_part,
        characterName: sr?.roles?.character_name || '',
        voiceType: sr?.roles?.voice_type || '',
        musicalId: sr?.roles?.musicals?.id || '',
        musicalTitle: sr?.roles?.musicals?.title || '',
        musicalPoster: sr?.roles?.musicals?.poster_url || ''
      }));
    } catch (error) {
      console.error('Error fetching song roles:', error);
      throw error;
    }
  },

  /**
   * Get character analyses for a song (for lesson access)
   * @param {string} songId - Song ID
   * @returns {Promise<Array>} List of character analyses with lesson content
   */
  async getCharacterAnalyses(songId) {
    try {
      const { data, error } = await supabase?.from('character_song_analyses')?.select(`
          id,
          role_id,
          overview,
          roles (
            id,
            character_name,
            voice_type
          )
        `)?.eq('song_id', songId);

      if (error) throw error;

      return (data || [])?.map(ca => ({
        id: ca?.id,
        roleId: ca?.role_id,
        overview: ca?.overview,
        characterName: ca?.roles?.character_name || '',
        voiceType: ca?.roles?.voice_type || ''
      }));
    } catch (error) {
      console.error('Error fetching character analyses:', error);
      throw error;
    }
  }
};